<template>
  <figure class="quality-photo" :class="{ 'quality-photo--compact': compact }">
    <button
      type="button"
      class="quality-photo__frame"
      :aria-label="zoomHint"
      @click="openViewer"
    >
      <img
        :src="photoUrl"
        :alt="accessibleAlt"
        class="quality-photo__img"
        loading="lazy"
        @error="onError"
      />
      <span class="quality-photo__zoom" aria-hidden="true">
        <f7-icon f7="arrow_up_left_arrow_down_right" size="16"></f7-icon>
      </span>
    </button>
    <figcaption class="quality-photo__hint">{{ zoomHint }}</figcaption>
  </figure>
</template>

<script setup>
/**
 * QualityPhoto — the leader's honest photo of the problem. Framed, tappable to
 * open the full-screen zoomable F7 photo browser. Photos are public `space`-disk
 * URLs (never data in query strings).
 *
 * No longer reason-gated: since the 2026-08-10 redesign a leader may attach a
 * photo to ANY question, not just a quality concern (BE→FE brief, and see
 * `ItemIssue::photoPath()`). The zoom is the capability, and it lives here so
 * there is exactly one `f7.photoBrowser` call site for this feature.
 */
import { f7 } from 'framework7-vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  photoUrl: {
    type: String,
    required: true,
  },
  // Item name → a meaningful alt (avoids a static alt string needing translation).
  alt: {
    type: String,
    default: '',
  },
  // The redesigned issue card's inline frame: 92px rather than ~180px. Not a
  // taste call — at full height the photo alone pushes the first answer control
  // past the 400px bar this design is measured against (spec Section 3, plate 03). The
  // detail is preserved by the zoom, which is a tap away either way.
  compact: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n({ useScope: 'global' });
const broken = ref(false);

const zoomHint = computed(() => t('collection.photo.zoomHint'));
// A standalone description (not just the item name) — a screen-reader user who
// jumps straight to the image (e.g. VoiceOver's image rotor) still hears whose
// photo it is and what it is of, without relying on the question paragraph read
// just before it in the card.
//
// It is ALSO the visible caption of the photo browser below, which is why the
// string is reason-neutral: it used to say "photo showing the quality issue
// with {item}", and since the 2026-08-10 redesign an admin may attach a photo
// to ANY question — so a supplier's price list on a price question was captioned
// as a quality problem, on screen, in words.
const accessibleAlt = computed(() => t('collection.photo.alt', { item: props.alt }));

function openViewer() {
  if (broken.value) return;
  f7.photoBrowser
    .create({
      photos: [{ url: props.photoUrl, caption: accessibleAlt.value || undefined }],
      type: 'standalone',
      theme: 'dark',
      toolbar: true,
      navbar: false,
      closeByBackdropClick: true,
    })
    .open();
}

function onError(event) {
  broken.value = true;
  event.target.style.display = 'none';
}
</script>

<style scoped>
.quality-photo {
  margin: 0;
}

.quality-photo__frame {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border: 1px solid var(--ci-border, #dce4e0);
  border-radius: var(--poolit-radius-lg, 14px);
  background: var(--ci-surface-soft, #eef2f0);
  overflow: hidden;
  cursor: zoom-in;
}

.quality-photo__frame:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ci-brand-ring, rgba(142, 91, 254, 0.45));
}

.quality-photo__img {
  display: block;
  width: 100%;
  max-height: 260px;
  object-fit: cover;
}

.quality-photo__zoom {
  position: absolute;
  inset-block-end: 8px;
  inset-inline-end: 8px;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: var(--poolit-radius-full, 999px);
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
}

.quality-photo__hint {
  margin: 6px 2px 0;
  font-size: var(--poolit-font-size-small, 0.78rem);
  color: var(--poolit-text-muted, #66746d);
  text-align: center;
}

/* Compact — the issue card's inline frame. */
.quality-photo--compact .quality-photo__frame {
  border-radius: var(--poolit-radius-lg);
}

.quality-photo--compact .quality-photo__img {
  height: 92px;
  max-height: 92px;
}

.quality-photo--compact .quality-photo__hint {
  margin-top: 4px;
  font-size: var(--poolit-font-size-2xs);
  text-align: start;
}
</style>
